import { Tag, Text } from '@factorialco/deprecated-design-system'
import {
  DataTableFilter,
  FilterType,
  FinanceJournalEntryFilter,
  ProjectionKind,
  useJournalEntryDatable,
} from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { Filter } from 'modules/core/components/DataTable/types'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  getJournalEntryStatusOptions,
  getJournalEntryTypeOptions,
  mapTypeText,
} from 'modules/finance/lib/journalEntries'

import Actions from './_Actions'
import { Amount } from './_Amount'
import { Status } from './_Status'

interface Props {
  legalEntityId?: string
  companyId: string
  defaultLegalEntityId?: string
  legalEntitiesOptions: { label: string; value: string }[]
  onFilterChange: (data: Record<string, unknown>) => void
  onClickDetail: (journalEntryId: string) => void
  canManageAccounts: boolean
  onExportClick?: () => void
}

const JournalEntryDatatable = ({
  legalEntityId,
  companyId,
  legalEntitiesOptions,
  onFilterChange,
  defaultLegalEntityId,
  onClickDetail,
  canManageAccounts,
  onExportClick,
}: Props) => {
  const onUpdateLegalEntityFilter = (filter?: Filter<string>) => {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    const filterValue = filter?.value as string | undefined
    // Clear filter
    if (filter === undefined || filterValue === undefined) {
      onFilterChange?.({ legalEntityId: undefined })
      return
    }
    onFilterChange?.({ legalEntityId: filterValue })
  }

  const source = useJournalEntryDatable(
    [
      {
        width: 150,
        identifier: 'number',
        title: i18n.t('finance.journal_entry.datatable.fields.number_v2'),
        kind: ProjectionKind.Path,
        path: 'number',
      },
      {
        identifier: 'publishedAt',
        title: i18n.t('finance.journal_entry.datatable.fields.recorded_at'),
        kind: ProjectionKind.Custom,
        sortBy: 'publishedAt',
        render: ({ publishedAt }) => (
          <Text.Normal size="200">
            {publishedAt ? dateBackendDateTimeToUI(String(publishedAt)) : '-'}
          </Text.Normal>
        ),
        template: '{{ publishedAt }}',
      },
      {
        identifier: 'type',
        title: i18n.t('finance.journal_entry.datatable.fields.type'),
        sortBy: 'type',
        kind: ProjectionKind.Custom,
        render: ({ type }) => <Text.Normal size="200">{mapTypeText(type)}</Text.Normal>,
        template: '{{ type }}',
      },
      {
        identifier: 'description',
        title: i18n.t('finance.journal_entry.datatable.fields.description'),
        kind: ProjectionKind.Path,
        path: 'description',
        colSpan: 2,
      },
      {
        identifier: 'status',
        title: i18n.t('finance.journal_entry.datatable.fields.status'),
        kind: ProjectionKind.Custom,
        sortBy: 'status',
        render: ({ status }) => <Status status={status} />,
        template: '{{ status }}',
      },
      {
        identifier: 'document',
        title: i18n.t('finance.journal_entry.datatable.fields.document'),
        kind: ProjectionKind.Custom,
        hidden: true,
        render: ({ financialDocument }) => (
          <Text.Normal size="200" ellipsis overflowWrap lineClamp={4}>
            {financialDocument ? financialDocument.documentNumber : '-'}
          </Text.Normal>
        ),
        template: '{{ invoice, bankTransaction }}',
      },
      {
        width: 140,
        identifier: 'lines',
        title: i18n.t('finance.journal_entry.datatable.fields.lines_v2'),
        kind: ProjectionKind.Custom,
        render: ({ journalLines }) => (
          <Tag type="grey" inline>
            {journalLines.length}
          </Tag>
        ),
        template: '{{ journalLines.size }}',
        tooltip: i18n.t('finance.journal_entry.datatable.fields.lines_tooltip'),
      },
      {
        identifier: 'debit',
        title: i18n.t('finance.journal_entry.datatable.fields.debit'),
        kind: ProjectionKind.Custom,
        render: ({ journalLines, legalEntity_currency }) => {
          const totalDebit = journalLines.reduce(
            (sum: number, line: { debitAmountCents: number }) => sum + line.debitAmountCents,
            0
          )
          return <Amount amountCents={totalDebit} currency={legalEntity_currency} />
        },
        template: '{{ - }}',
      },
      {
        identifier: 'credit',
        title: i18n.t('finance.journal_entry.datatable.fields.credit'),
        kind: ProjectionKind.Custom,
        render: ({ journalLines, legalEntity_currency }) => {
          const totalCredit = journalLines.reduce(
            (sum: number, line: { creditAmountCents: number }) => sum + line.creditAmountCents,
            0
          )

          return <Amount amountCents={totalCredit} currency={legalEntity_currency} />
        },
        template: '{{ - }}',
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 50,
        render: ({ id }: { id: string }) => {
          return <Actions journalEntryId={id} onClickDetail={onClickDetail} />
        },
      },
    ],
    {
      variables: { legalEntityIds: legalEntityId ? [legalEntityId] : [], companyId: companyId },
    }
  )

  const filters: DataTableFilter<FinanceJournalEntryFilter>[] = [
    {
      identifier: 'legal_entity_id',
      title: i18n.t('finance.workspace.legal_entity_scope.selector_label'),
      path: 'legalEntityId',
      default: !!defaultLegalEntityId,
      type: FilterType.Id,
      onUpdateValue: onUpdateLegalEntityFilter,
      categories: [
        {
          kind: 'SingleChoice',
          operation: 'in',
          value: defaultLegalEntityId,
          options: legalEntitiesOptions,
        },
      ],
    },
    {
      identifier: 'published_at',
      title: i18n.t('finance.journal_entry.datatable.fields.recorded_at'),
      path: 'publishedAt',
      type: FilterType.Date,
      categories: [
        {
          kind: 'DatePicker',
          operation: 'between',
        },
      ],
    },
    {
      identifier: 'status',
      title: i18n.t('finance.journal_entry.datatable.fields.status'),
      path: 'status',
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: getJournalEntryStatusOptions(),
        },
      ],
    },
    {
      identifier: 'type',
      title: i18n.t('finance.journal_entry.datatable.fields.type'),
      path: 'type',
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: getJournalEntryTypeOptions(),
        },
      ],
    },
  ]

  return (
    <DataTable
      disableExport
      source={source}
      pagination={{ kind: 'offset', itemsPerPage: 25 }}
      sort={{ path: 'number', direction: 'asc' }}
      customPopoverOptions={
        canManageAccounts ? (
          <PopoverMenu.TouchableOption
            key="finance.chart_of_accounts.export_ledger"
            onClick={onExportClick}
            title={i18n.t('common.export')}
          />
        ) : undefined
      }
      filters={filters}
    />
  )
}

export default JournalEntryDatatable
